import { Icon, IconButton, Tooltip } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useNotification } from '@hero/store/hooks';

import { AddTagsTable } from './AddTagsTable';
import { RemoveTagsTable } from './RemoveTagsTable';
import * as S from './style';

function DoubleTagTable({
  selectedRows,
  onChangeSelectedRows,
  defaultSelectedTags,
  disabledColumns = [],
  showAddTags,
  isModal = false,
  isEditContactsModalOpen,
  setIsEditContactModal,
  isDeleteActionShown
}) {
  const { t } = useTranslation();
  const { showSuccess } = useNotification();

  const [addSelectedTagRows, setAddSelectedTagRows] = useState({});
  const [removeSelectedTagRows, setRemoveSelectedTagRows] = useState({});

  const selectedRowArray = useMemo(() => {
    return Object.keys(selectedRows || {});
  }, [selectedRows]);

  const removeTagSelectedRowsArray = useMemo(() => {
    return Object.keys(removeSelectedTagRows || {});
  }, [removeSelectedTagRows]);

  const addTagSelectedRowsArray = useMemo(() => {
    return Object.keys(addSelectedTagRows || {});
  }, [addSelectedTagRows]);

  const onAddTag = () => {
    onChangeSelectedRows({
      ...selectedRows,
      ...addSelectedTagRows
    });

    if (!isModal)
      showSuccess({
        type: 'toast',
        message: t('typeSuccessfullyAddedToFeature', {
          count: Object.keys(addSelectedTagRows)?.length,
          type: t('tag').toLocaleLowerCase(),
          feature: t('automation').toLocaleLowerCase()
        })
      });

    setAddSelectedTagRows(() => ({}));
  };

  const onRemoveTag = () => {
    selectedRowArray.forEach(selectedRow => {
      if (removeSelectedTagRows[selectedRow]) {
        delete selectedRows[selectedRow];
      }
    });

    onChangeSelectedRows({ ...selectedRows });

    if (!isModal)
      showSuccess({
        type: 'toast',
        message: t('typeSuccessfullyRemovedFromFeature', {
          count: Object.keys(removeSelectedTagRows)?.length,
          type: t('tag').toLocaleLowerCase(),
          feature: t('automation').toLocaleLowerCase()
        })
      });

    setRemoveSelectedTagRows(() => ({}));
  };

  return (
    <S.DoubleTagsWrapper>
      <AddTagsTable
        selectedRows={addSelectedTagRows}
        disabledRows={selectedRows}
        onChangeSelectedRows={setAddSelectedTagRows}
        showAddTags={showAddTags}
        defaultSelectedTags={defaultSelectedTags}
        disabledColumns={disabledColumns}
        isEditContactsModalOpen={isEditContactsModalOpen}
        setIsEditContactModal={setIsEditContactModal}
        isDeleteActionShown={isDeleteActionShown}
      />
      <S.NavigationButtons>
        <Tooltip content={t('addTagsToAutomation')}>
          <S.NavigationButton>
            <IconButton
              color={addTagSelectedRowsArray.length === 0 ? 'secondary' : 'primary'}
              onClick={onAddTag}
              rounded={false}
              size="lg"
              disabled={addTagSelectedRowsArray.length === 0}
            >
              <Icon name="next" />
            </IconButton>
            {t('add')}
          </S.NavigationButton>
        </Tooltip>

        <Tooltip content={t('removeTagsFromAutomation')}>
          <S.NavigationButton>
            <IconButton
              color={removeTagSelectedRowsArray.length === 0 ? 'secondary' : 'primary'}
              onClick={onRemoveTag}
              rounded={false}
              size="lg"
              disabled={removeTagSelectedRowsArray.length === 0}
            >
              <Icon name="prev" />
            </IconButton>
            {t('remove')}
          </S.NavigationButton>
        </Tooltip>
      </S.NavigationButtons>
      <RemoveTagsTable
        totalTagData={selectedRowArray}
        selectedRows={removeSelectedTagRows}
        onChangeSelectedRows={setRemoveSelectedTagRows}
        defaultSelectedTags={defaultSelectedTags}
        disabledColumns={disabledColumns}
      />
    </S.DoubleTagsWrapper>
  );
}

DoubleTagTable.S = S;

DoubleTagTable.propTypes = {
  selectedRows: PropTypes.array,
  onChangeSelectedRows: PropTypes.func,
  defaultSelectedTags: PropTypes.array,
  disabledColumns: PropTypes.array,
  showAddTags: PropTypes.bool,
  isModal: PropTypes.bool,
  isEditContactsModalOpen: PropTypes.bool,
  setIsEditContactModal: PropTypes.func,
  isDeleteActionShown: PropTypes.bool
};

export { DoubleTagTable };
